import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { DEFAULT_H3_CONFIG, resolveSpatialBucket } from "../h3SpatialUtils";
import { 
  Clock, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Users,
  Car,
  AlertCircle,
  BarChart,
  LineChart,
  Zap,
  Sun,
  Moon,
  ChevronUp,
  ChevronDown
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-500" size={20} />
          <h3 className="text-lg font-semibold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <ChevronUp className="text-blue-500" size={20} /> : <ChevronDown className="text-blue-500" size={20} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-800 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const PeakHoursReport = () => {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [rideshareRoutes, setRideshareRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({});
  const [h3Config, setH3Config] = useState(DEFAULT_H3_CONFIG);

  const toggleH3 = () => {
    setH3Config((current) => ({
      ...current,
      enabled: !current.enabled,
      status: !current.enabled ? "active" : "disabled",
    }));
  };

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null;
    }
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [tripsRes, driversRes, customersRes, rideshareRequestsRes, rideshareRoutesRes] = await Promise.all([
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/customerdetails/`),
        fetch(`${API_URL}/rideshare/requests/all`),
        fetch(`${API_URL}/rideshare/trips`)
      ]);

      const [tripsData, driversData, customersData, rideshareRequestsData, rideshareRoutesData] = await Promise.all([
        tripsRes.json(),
        driversRes.json(),
        customersRes.json(),
        rideshareRequestsRes.json(),
        rideshareRoutesRes.json()
      ]);

      setTrips(tripsData || []);
      setDrivers(driversData || []);
      setCustomers(customersData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setRideshareRoutes(Array.isArray(rideshareRoutesData) ? rideshareRoutesData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Process time-based data
  const timeAnalysis = useMemo(() => {
    const dateRange = getDateRange(timeFilter);
    // `/trip` is exclusively the private-ride table — every row here is a private trip,
    // there's no `trip_type`/`rideshare_id` field on these rows to filter by.
    const privateTrips = filterTripsByDate(trips, dateRange);

    // Rideshare activity/revenue lives on accepted `rideshare_requests` rows (one per passenger,
    // `accepted_amount` is the fare-of-record), not on `/trip`. This report has no per-driver
    // breakdown, so the `/rideshare/trips` route data isn't needed for a join here.
    const filteredRideshareRequests = (dateRange
      ? rideshareRequests.filter(req => {
          if (!req.created_at) return false;
          const d = new Date(req.created_at);
          return d >= dateRange.start && d <= dateRange.end;
        })
      : rideshareRequests
    ).filter(req => (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0) > 0);

    // Normalize rideshare requests into trip-like records so they can flow through the same
    // hour/day/time-slot bucketing logic (processTrips) as private trips.
    const rideshareTrips = filteredRideshareRequests.map(req => ({
      ...req,
      delivery_cost_proposed: parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0,
      order_start_datetime: req.created_at
    }));

    const filteredTrips = [...privateTrips, ...rideshareTrips];

    const spatialBucketData = {};

    const getSpatialBucket = (trip, fallback) => {
      const resolved = resolveSpatialBucket(
        {
          pickup_h3: trip?.pickup_h3 || trip?.pickup_hex || trip?.pickup_h3_index || trip?.origin_h3,
          dropoff_h3: trip?.dropoff_h3 || trip?.dropoff_hex || trip?.dropoff_h3_index || trip?.destination_h3,
        },
        fallback,
        h3Config
      );

      return resolved.bucket;
    };

    // Initialize time-based data structures for all trips
    const hourlyData = {};
    const dailyData = {};
    const weeklyData = {};
    const timeSlotData = {
      'Early Morning (12AM-6AM)': { count: 0, revenue: 0, trips: [] },
      'Morning Rush (6AM-9AM)': { count: 0, revenue: 0, trips: [] },
      'Mid Morning (9AM-12PM)': { count: 0, revenue: 0, trips: [] },
      'Afternoon (12PM-5PM)': { count: 0, revenue: 0, trips: [] },
      'Evening Rush (5PM-8PM)': { count: 0, revenue: 0, trips: [] },
      'Night (8PM-12AM)': { count: 0, revenue: 0, trips: [] }
    };

    // Initialize time-based data structures for private trips
    const privateHourlyData = {};
    const privateDailyData = {};
    const privateTimeSlotData = {
      'Early Morning (12AM-6AM)': { count: 0, revenue: 0, trips: [] },
      'Morning Rush (6AM-9AM)': { count: 0, revenue: 0, trips: [] },
      'Mid Morning (9AM-12PM)': { count: 0, revenue: 0, trips: [] },
      'Afternoon (12PM-5PM)': { count: 0, revenue: 0, trips: [] },
      'Evening Rush (5PM-8PM)': { count: 0, revenue: 0, trips: [] },
      'Night (8PM-12AM)': { count: 0, revenue: 0, trips: [] }
    };

    // Initialize time-based data structures for rideshare trips
    const rideshareHourlyData = {};
    const rideshareDailyData = {};
    const rideshareTimeSlotData = {
      'Early Morning (12AM-6AM)': { count: 0, revenue: 0, trips: [] },
      'Morning Rush (6AM-9AM)': { count: 0, revenue: 0, trips: [] },
      'Mid Morning (9AM-12PM)': { count: 0, revenue: 0, trips: [] },
      'Afternoon (12PM-5PM)': { count: 0, revenue: 0, trips: [] },
      'Evening Rush (5PM-8PM)': { count: 0, revenue: 0, trips: [] },
      'Night (8PM-12AM)': { count: 0, revenue: 0, trips: [] }
    };

    // Function to process trips for a given dataset
    const processTrips = (trips, hourlyData, dailyData, timeSlotData) => {
      trips.forEach(trip => {
        const revenue = parseFloat(trip.delivery_cost_proposed) || 0;
        const tripDate = new Date(trip.order_start_datetime);
        const spatialBucket = getSpatialBucket(
          trip,
          trip.pickup_location || trip.pickup_address || trip.origin_name || 'Unknown Location'
        );

        if (h3Config.enabled && spatialBucket && spatialBucket !== 'Unknown Location') {
          if (!spatialBucketData[spatialBucket]) {
            spatialBucketData[spatialBucket] = { count: 0, revenue: 0, trips: [] };
          }
          spatialBucketData[spatialBucket].count++;
          spatialBucketData[spatialBucket].revenue += revenue;
          spatialBucketData[spatialBucket].trips.push(trip);
        }
        
        // Extract time components
        const hour = tripDate.getHours();
        const dayOfWeek = tripDate.getDay(); // 0 = Sunday, 6 = Saturday
        const dayOfMonth = tripDate.getDate();
        const month = tripDate.getMonth();
        
        // Hourly analysis
        if (!hourlyData[hour]) {
          hourlyData[hour] = {
            count: 0,
            revenue: 0,
            avgRevenue: 0,
            trips: []
          };
        }
        hourlyData[hour].count++;
        hourlyData[hour].revenue += revenue;
        hourlyData[hour].trips.push(trip);

        // Daily analysis (day of week)
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[dayOfWeek];
        if (!dailyData[dayName]) {
          dailyData[dayName] = {
            count: 0,
            revenue: 0,
            avgRevenue: 0,
            trips: []
          };
        }
        dailyData[dayName].count++;
        dailyData[dayName].revenue += revenue;
        dailyData[dayName].trips.push(trip);

        // Time slot analysis
        let timeSlot;
        if (hour >= 0 && hour < 6) timeSlot = 'Early Morning (12AM-6AM)';
        else if (hour >= 6 && hour < 9) timeSlot = 'Morning Rush (6AM-9AM)';
        else if (hour >= 9 && hour < 12) timeSlot = 'Mid Morning (9AM-12PM)';
        else if (hour >= 12 && hour < 17) timeSlot = 'Afternoon (12PM-5PM)';
        else if (hour >= 17 && hour < 20) timeSlot = 'Evening Rush (5PM-8PM)';
        else timeSlot = 'Night (8PM-12AM)';

        timeSlotData[timeSlot].count++;
        timeSlotData[timeSlot].revenue += revenue;
        timeSlotData[timeSlot].trips.push(trip);
      });
    };

    // Process all trips
    processTrips(filteredTrips, hourlyData, dailyData, timeSlotData);
    
    // Process private trips
    processTrips(privateTrips, privateHourlyData, privateDailyData, privateTimeSlotData);
    
    // Process rideshare trips
    processTrips(rideshareTrips, rideshareHourlyData, rideshareDailyData, rideshareTimeSlotData);

    // Calculate averages for all datasets
    const calculateAverages = (data) => {
      Object.keys(data).forEach(key => {
        const item = data[key];
        item.avgRevenue = item.count > 0 ? item.revenue / item.count : 0;
      });
    };

    calculateAverages(hourlyData);
    calculateAverages(dailyData);
    calculateAverages(timeSlotData);
    calculateAverages(privateHourlyData);
    calculateAverages(privateDailyData);
    calculateAverages(privateTimeSlotData);
    calculateAverages(rideshareHourlyData);
    calculateAverages(rideshareDailyData);
    calculateAverages(rideshareTimeSlotData);

    return {
      filteredTrips,
      privateTrips,
      rideshareTrips,
      hourlyData,
      dailyData,
      weeklyData,
      timeSlotData,
      privateHourlyData,
      privateDailyData,
      privateTimeSlotData,
      rideshareHourlyData,
      rideshareDailyData,
      rideshareTimeSlotData,
      spatialBucketData,
      totalRevenue: filteredTrips.reduce((sum, trip) => sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0)
    };
  }, [trips, rideshareRequests, timeFilter, h3Config]);

  // Get top performers
  const getTopPerformers = (data, metric, limit = 20) => {
    return Object.entries(data)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b[metric] - a[metric])
      .slice(0, limit);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename) => {
    const csv = [
      ['Time Period', 'Trips', 'Revenue', 'Average Revenue'],
      ...data.map(item => [
        item.name,
        item.count || 0,
        item.revenue?.toFixed(2) || 0,
        item.avgRevenue?.toFixed(2) || 0
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Get peak hours
  const getPeakHours = () => {
    const hourlyEntries = Object.entries(timeAnalysis.hourlyData).map(([hour, data]) => ({
      hour: parseInt(hour),
      ...data
    })).sort((a, b) => b.count - a.count);

    return hourlyEntries.slice(0, 5);
  };

  // Get peak days
  const getPeakDays = () => {
    return getTopPerformers(timeAnalysis.dailyData, 'count', 7);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const { filteredTrips, privateTrips, rideshareTrips, hourlyData, dailyData, weeklyData, timeSlotData, privateHourlyData, privateDailyData, privateTimeSlotData, rideshareHourlyData, rideshareDailyData, rideshareTimeSlotData, spatialBucketData, totalRevenue } = timeAnalysis;
  const peakHours = getPeakHours();
  const peakDays = getPeakDays();
  const h3StatusLabel = h3Config.enabled ? 'H3 spatial bins active' : 'H3 disabled: using fallback time buckets';

  // Get peak hours for private and rideshare
  const getPrivatePeakHours = () => {
    const hourlyEntries = Object.entries(privateHourlyData).map(([hour, data]) => ({
      hour: parseInt(hour),
      ...data
    })).sort((a, b) => b.count - a.count);
    return hourlyEntries.slice(0, 5);
  };

  const getRidesharePeakHours = () => {
    const hourlyEntries = Object.entries(rideshareHourlyData).map(([hour, data]) => ({
      hour: parseInt(hour),
      ...data
    })).sort((a, b) => b.count - a.count);
    return hourlyEntries.slice(0, 5);
  };

  const privatePeakHours = getPrivatePeakHours();
  const ridesharePeakHours = getRidesharePeakHours();

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Peak Hours Analysis
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive time-based analysis of trips, revenue, and demand patterns
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="text-gray-500" size={20} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getFilterLabel(timeFilter)}
              </span>
            </div>
          </div>

          {/* Time Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "7days", label: "Past 7 Days" },
              { value: "14days", label: "Past 14 Days" },
              { value: "30days", label: "Past 30 Days" }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  timeFilter === filter.value
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="font-semibold">H3 spatial indexing</div>
              <div>{h3StatusLabel}</div>
            </div>
            <button
              type="button"
              onClick={toggleH3}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                h3Config.enabled
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-800 hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-100'
              }`}
            >
              {h3Config.enabled ? 'Disable H3' : 'Enable H3'}
            </button>
          </div>
        </div>

        {/* Report Tooltip */}
        <ReportTooltip title="Peak Hours Analysis - Business Value & Features">
          <div className="space-y-3">
            <div>
              <strong className="text-blue-900">⏰ Report Overview:</strong>
              <p>Comprehensive time-based analysis of trips, revenue, and demand patterns with separate insights for Private and Rideshare services.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🎯 Key Business Questions Answered:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Operational Planning:</strong> When are my peak hours? When do I need more drivers? What are the busiest times?</li>
                <li><strong>Service Optimization:</strong> When do private rides peak vs rideshare? Which service is busier at different times?</li>
                <li><strong>Revenue Strategy:</strong> When do we make most money? Should I implement surge pricing during peak hours?</li>
                <li><strong>Resource Allocation:</strong> How should I schedule drivers? When should I run promotions?</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">✅ Key Features:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Time-Based Analysis:</strong> Hourly, daily, weekly, and time slot breakdowns</li>
                <li><strong>Service Separation:</strong> Distinct Private and Rideshare peak hours analysis</li>
                <li><strong>Revenue Tracking:</strong> Revenue patterns by time and service type</li>
                <li><strong>Visual Heatmaps:</strong> Color-coded hourly intensity visualization</li>
                <li><strong>Export Functionality:</strong> CSV download for all time-based data</li>
                <li><strong>Real-Time Data:</strong> Live API calls with 30-second refresh intervals</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">💼 Business Impact:</strong>
              <p>This report provides immediate operational value by helping you optimize driver scheduling, implement dynamic pricing, improve service availability during peak demand, and maximize revenue through strategic time-based decisions.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🔍 Data Source:</strong>
              <p>Uses REAL data from live API calls to trips, rideshare requests, drivers, and customers endpoints. Private-ride timestamps come from each trip's <code>order_start_datetime</code>; rideshare timestamps and revenue come from each accepted passenger request's <code>created_at</code> and agreed fare.</p>
            </div>
          </div>
        </ReportTooltip>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Trips {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {filteredTrips.length}
                </p>
              </div>
              <Activity className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Revenue {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${totalRevenue.toFixed(0)}
                </p>
              </div>
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Private Trips</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {privateTrips.length}
                </p>
              </div>
              <Car className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Rideshare Trips</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {rideshareTrips.length}
                </p>
              </div>
              <Users className="text-orange-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Peak Hour</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {peakHours[0] ? `${peakHours[0].hour}:00` : 'N/A'}
                </p>
              </div>
              <Zap className="text-yellow-500" size={24} />
            </div>
          </div>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Peak Hours */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Zap className="mr-2 text-yellow-500" />
                Peak Hours Analysis
              </div>
              <button
                onClick={() => exportData(peakHours, 'peak-hours.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {peakHours.map((hourData, index) => (
                    <div key={hourData.hour} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Clock className="text-yellow-500" size={20} />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {hourData.hour}:00 - {hourData.hour + 1}:00
                          </span>
                        </div>
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm font-medium">
                          #{index + 1}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Trips: {hourData.count}</p>
                        <p>Revenue: ${hourData.revenue.toFixed(2)}</p>
                        <p>Avg: ${hourData.avgRevenue.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Private Rides Peak Hours */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Car className="mr-2 text-blue-500" />
                Private Rides Peak Hours
              </div>
              <button
                onClick={() => exportData(privatePeakHours, 'private-peak-hours.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {privatePeakHours.map((hourData, index) => (
                    <div key={hourData.hour} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Clock className="text-blue-500" size={20} />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {hourData.hour}:00 - {hourData.hour + 1}:00
                          </span>
                        </div>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          #{index + 1}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Trips: {hourData.count}</p>
                        <p>Revenue: ${hourData.revenue.toFixed(2)}</p>
                        <p>Avg: ${hourData.avgRevenue.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Rideshare Peak Hours */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Users className="mr-2 text-orange-500" />
                Rideshare Peak Hours
              </div>
              <button
                onClick={() => exportData(ridesharePeakHours, 'rideshare-peak-hours.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {ridesharePeakHours.map((hourData, index) => (
                    <div key={hourData.hour} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Clock className="text-orange-500" size={20} />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {hourData.hour}:00 - {hourData.hour + 1}:00
                          </span>
                        </div>
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-medium">
                          #{index + 1}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Trips: {hourData.count}</p>
                        <p>Revenue: ${hourData.revenue.toFixed(2)}</p>
                        <p>Avg: ${hourData.avgRevenue.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Time Slots Analysis */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <BarChart className="mr-2 text-blue-500" />
                Time Slots Analysis
              </div>
              <button
                onClick={() => exportData(Object.entries(timeSlotData).map(([name, data]) => ({ name, ...data })), 'time-slots.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(timeSlotData).map(([slotName, slotData]) => (
                    <div key={slotName} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {slotName.includes('Morning') ? <Sun className="text-orange-500" size={20} /> :
                           slotName.includes('Night') ? <Moon className="text-indigo-500" size={20} /> :
                           <Clock className="text-blue-500" size={20} />}
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {slotName}
                          </span>
                        </div>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          {slotData.count}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Revenue: ${slotData.revenue.toFixed(2)}</p>
                        <p>Avg: ${slotData.avgRevenue.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Daily Analysis */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Sun className="mr-2 text-orange-500" />
                Daily Analysis
              </div>
              <button
                onClick={() => exportData(getPeakDays(), 'daily-analysis.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Day</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Busiest Hour</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {getPeakDays().map((dayData, index) => {
                      // Find busiest hour for this day
                      const dayTrips = dayData.trips || [];
                      const hourCounts = {};
                      dayTrips.forEach(trip => {
                        const hour = new Date(trip.order_start_datetime).getHours();
                        hourCounts[hour] = (hourCounts[hour] || 0) + 1;
                      });
                      const busiestHour = Object.entries(hourCounts)
                        .sort(([,a], [,b]) => b - a)[0];
                      
                      return (
                        <tr key={dayData.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            <div className="flex items-center">
                              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium mr-2">
                                #{index + 1}
                              </span>
                              {dayData.name}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{dayData.count}</td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">${dayData.revenue.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${dayData.avgRevenue.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {busiestHour ? `${busiestHour[0]}:00 (${busiestHour[1]} trips)` : 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Hourly Heatmap */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <BarChart3 className="mr-2 text-purple-500" />
              24-Hour Activity Heatmap
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const hourData = hourlyData[hour];
                    const tripCount = hourData ? hourData.count : 0;
                    const maxTrips = Math.max(...Object.values(hourlyData).map(h => h.count));
                    const intensity = maxTrips > 0 ? (tripCount / maxTrips) : 0;
                    
                    let bgColor = 'bg-gray-100';
                    if (intensity > 0.8) bgColor = 'bg-red-500';
                    else if (intensity > 0.6) bgColor = 'bg-orange-500';
                    else if (intensity > 0.4) bgColor = 'bg-yellow-500';
                    else if (intensity > 0.2) bgColor = 'bg-green-500';
                    else if (intensity > 0) bgColor = 'bg-blue-500';
                    
                    return (
                      <div
                        key={hour}
                        className={`${bgColor} text-white rounded p-2 text-center`}
                        title={`${hour}:00 - ${tripCount} trips`}
                      >
                        <div className="text-xs font-medium">{hour}:00</div>
                        <div className="text-xs">{tripCount}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center space-x-4 mt-4 text-xs text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-gray-100 rounded"></div>
                    <span>No Activity</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span>Low</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span>Medium</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                    <span>High</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-orange-500 rounded"></div>
                    <span>Very High</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-red-500 rounded"></div>
                    <span>Peak</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PeakHoursReport;
